/**
 * Plan prices as the Api sends them (Warren, 21 October 2026, night: "Read both prices from the live Stripe Price
 * objects, never calculate one from the other. Use the same source everywhere the plan appears"): each cadence of the
 * network plan, the fleet program (base and per charger) and load management from its own Stripe Price, null where
 * Stripe has none. Nothing in the Admin holds a plan price or turns one cadence into the other; a missing price reads
 * "EV Initiative confirms the price". Pure, so it is unit-tested (planPrices.test.ts).
 */
export interface PlanPriceView { cadence: 'annual' | 'monthly'; unit_price_cents: number; currency: string; interval: 'month' | 'year' }
export interface PlanPricePair { annual: PlanPriceView | null; monthly: PlanPriceView | null }
export const PLAN_PRICE_UNCONFIRMED = 'EV Initiative confirms the price';

/** "US$80.00 per charger per year", in the caller's money format, or "EV Initiative confirms the price". */
export function planPriceLine(price: Pick<PlanPriceView, 'unit_price_cents' | 'currency' | 'interval'> | null | undefined, format: (cents: number, currency: string) => string, per = 'per charger'): string {
  return price ? `${format(price.unit_price_cents, price.currency)} ${per} per ${price.interval}` : PLAN_PRICE_UNCONFIRMED;
}

/** The same line from a price sent as cents and its cadence (the customer's pay page), or EV Initiative confirms it. */
export function cadencePriceLine(cents: number | null | undefined, cadence: 'annual' | 'monthly', format: (cents: number) => string, per = 'per charger'): string {
  return cents != null ? `${format(cents)} ${per} per ${cadence === 'annual' ? 'year' : 'month'}` : PLAN_PRICE_UNCONFIRMED;
}

/** The circuit prompts' load management price for the account's cadence: "$4.00 per managed charger per month". */
export function dlmAddonPriceLine(addon: { price_cents_per_charger: number | null; price_interval?: 'month' | 'year' | null; currency?: string | null },
  format: (cents: number, currency: string) => string): string {
  return addon.price_cents_per_charger != null && addon.price_interval
    ? `${format(addon.price_cents_per_charger, addon.currency ?? 'USD')} per managed charger per ${addon.price_interval}` : PLAN_PRICE_UNCONFIRMED;
}

/**
 * A plan's cost at one cadence: each part's price (the fleet base once, a price per charger times the chargers) from
 * that cadence's own Price, added in exact cents. Null when any part is unpriced: the screen then says EV Initiative
 * confirms the price rather than a number.
 */
export function planCost(parts: Array<{ price: PlanPriceView | null | undefined; quantity: number }>): number | null {
  let cents = 0;
  for (const p of parts) {
    if (!p.price) return null;
    cents += p.price.unit_price_cents * p.quantity;
  }
  return cents;
}
